/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Text, Stack } from 'office-ui-fabric-react';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { List } from 'office-ui-fabric-react/lib/List';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import styles from './AdjustmentActivityItems.style';
import { can } from '../../utils/auth';
import {
  getDisplayableLineItemChangesForReversals,
  calculateLineItemStatus,
} from '../../utils/adjustmentUtils';

const AdjustmentAction = ({ action }) => {
  const { newData } = action;
  const initiatedName = can('viewActivityInitiatedBy')
    ? action?.initiatedBy?.name || action?.initiatedBy
    : 'Microsoft';

  const adjustmentName = newData.name || 'the adjustment';

  switch (action.operation) {
    case 'created': {
      return (
        <Text className={styles.itemName}>
          <Text>{initiatedName}</Text>
          <Text className={styles.fontWeightNormal}> saved </Text>
          <Text>{adjustmentName}</Text>
          <Text className={styles.fontWeightNormal}> as a draft </Text>
        </Text>
      );
    }

    case 'edited': {
      return (
        <Text className={styles.itemName}>
          <Text>{initiatedName}</Text>
          <Text className={styles.fontWeightNormal}> saved edits to </Text>
          <Text>{adjustmentName}</Text>
          <Text className={styles.fontWeightNormal}> as a draft </Text>
        </Text>
      );
    }

    case 'submit-for-reviewer-approval': {
      return (
        <Stack>
          <Stack.Item>
            <Text className={styles.itemName}>
              <Text>{initiatedName}</Text>
              <Text className={styles.fontWeightNormal}> submitted </Text>
              <Text>{adjustmentName}</Text>
              <Text className={styles.fontWeightNormal}> for review </Text>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Stack>
              <Stack.Item>
                <Text as="p" variant="small">
                  Reviewer:{' '}
                  <span className={styles.bold}>
                    {action.newData?.reviewer?.name ||
                      action.newData?.reviewer?.email ||
                      action.newData?.reviewer?.reviewerId}
                  </span>
                </Text>
              </Stack.Item>

              <Stack.Item>
                <Text as="p" variant="small">
                  Reviewer Justification:{' '}
                  <span className={styles.bold}>
                    {action.newData?.reviewer?.reviewerJustification}
                  </span>
                </Text>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      );
    }

    case 'submit-for-approver-approval': {
      return (
        <Stack>
          <Stack.Item>
            <Text className={styles.itemName}>
              <Text>{initiatedName}</Text>
              <Text className={styles.fontWeightNormal}> submitted </Text>
              <Text>{adjustmentName}</Text>
              <Text className={styles.fontWeightNormal}> for approval </Text>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Stack>
              <Stack.Item>
                <Text as="p" variant="small">
                  Approver:{' '}
                  <span className={styles.bold}>
                    {action.newData?.approver?.name ||
                      action.newData?.approver?.email ||
                      action.newData?.approver?.approverId}
                  </span>
                </Text>
              </Stack.Item>

              <Stack.Item>
                <Text as="p" variant="small">
                  Approver Justification:{' '}
                  <span className={styles.bold}>
                    {action.newData?.approver?.approverJustification}
                  </span>
                </Text>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      );
    }

    case 'reject': {
      return (
        <Stack>
          <Stack.Item>
            <Text className={styles.itemName}>
              <Text>{initiatedName}</Text>
              <Text className={styles.fontWeightNormal}> rejected </Text>
              <Text>{adjustmentName}</Text>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Text as="p" variant="small">
              Reject Reason: <span className={styles.bold}>{action.newData?.rejectReason}</span>
            </Text>
          </Stack.Item>
        </Stack>
      );
    }

    case 'approve':
    case 'oneTimePayment-approved': {
      // oneTimePayment-approved is used for generating invoice. But from acitityLog perspective, it is still an approve operation
      return (
        <Text className={styles.itemName}>
          <Text>{initiatedName}</Text>
          <Text className={styles.fontWeightNormal}> approved </Text>
          <Text>{adjustmentName}</Text>
        </Text>
      );
    }

    case 'reversal-save-draft': {
      if (newData.lineItems.length === 0) {
        return (
          <Text className={styles.itemName}>
            <Text>{initiatedName}</Text>
            <Text className={styles.fontWeightNormal}> discarded reversal edits to </Text>
            <Text>{adjustmentName}</Text>
          </Text>
        );
      }

      return (
        <Text className={styles.itemName}>
          <Text>{initiatedName}</Text>
          <Text className={styles.fontWeightNormal}> saved </Text>
          <Text>{adjustmentName}</Text>
          <Text className={styles.fontWeightNormal}> reversal as draft </Text>
        </Text>
      );
    }

    case 'reversal-submit-for-approval': {
      const adjustment = {
        ...newData,
        lineItems: calculateLineItemStatus(newData),
      };
      const lineItemChanges = getDisplayableLineItemChangesForReversals(adjustment);

      return (
        <Stack>
          <Stack.Item>
            <Text className={styles.itemName}>
              <Text>{initiatedName}</Text>
              <Text className={styles.fontWeightNormal}> submitted </Text>
              <Text>{adjustmentName}</Text>
              <Text className={styles.fontWeightNormal}> reversal for review </Text>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Stack>
              {lineItemChanges.map(lineItemChange => (
                <Stack.Item key={lineItemChange._id}>
                  <Stack>
                    {lineItemChange.title && (
                      <Stack.Item>
                        <Text as="p" variant="small" className={styles.bold}>
                          {lineItemChange.title}
                        </Text>
                      </Stack.Item>
                    )}
                    {lineItemChange.changes.map(change => (
                      <Stack.Item key={change}>
                        <Text
                          key={change}
                          as="p"
                          variant="small"
                          style={{ fontWeight: 400, marginLeft: '0.75em' }}
                        >
                          {change}
                        </Text>
                      </Stack.Item>
                    ))}
                  </Stack>
                </Stack.Item>
              ))}
            </Stack>
          </Stack.Item>
        </Stack>
      );
    }

    case 'reversal-reject': {
      return (
        <Stack>
          <Stack.Item>
            <Text className={styles.itemName}>
              <Text>{initiatedName}</Text>
              <Text className={styles.fontWeightNormal}> rejected </Text>
              <Text>{adjustmentName}</Text>
              <Text className={styles.fontWeightNormal}> reversal </Text>
            </Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p" variant="small">
              Reject Reason: <span className={styles.bold}>{action.newData?.rejectReason}</span>
            </Text>
          </Stack.Item>
        </Stack>
      );
    }

    case 'reversal-approve': {
      return (
        <Text className={styles.itemName}>
          <Text>{initiatedName}</Text>
          <Text className={styles.fontWeightNormal}> approved </Text>
          <Text>{adjustmentName}</Text>
          <Text className={styles.fontWeightNormal}> reversal </Text>
        </Text>
      );
    }

    default:
      return null;
  }
};

const ActivityItem = item => {
  return (
    <div className={styles.activityItem} data-is-focusable>
      <Persona
        imageUrl={can('viewActivityInitiatedBy') ? '' : '/images/msft-avatar.png'}
        size={PersonaSize.size28}
        className={styles.personaCoin}
        hidePersonaDetails
      />

      <div className={styles.itemContent}>
        <AdjustmentAction action={item} />
      </div>
      <Text as="p" variant="small" className={styles.date}>
        {item.timestamp && new Date(item.timestamp).toDateString().split(' ').slice(1).join(' ')}
      </Text>
    </div>
  );
};

const AdjustmentActivityItems = ({ items }) => (
  <FocusZone direction={FocusZoneDirection.vertical}>
    <List items={items} onRenderCell={ActivityItem} />
  </FocusZone>
);

AdjustmentActivityItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
      initiatedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
      operation: PropTypes.string.isRequired,
      area: PropTypes.string.isRequired,
      newData: PropTypes.shape({
        address: PropTypes.string,
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
        contractId: PropTypes.string,
      }),
      oldData: PropTypes.shape({
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
      }),
      params: PropTypes.shape({
        contractAddress: PropTypes.string,
      }),
    }),
  ),
};

AdjustmentActivityItems.defaultProps = {
  items: [],
};

export default AdjustmentActivityItems;
